import { AssignmentIcon } from '@components/UI-Components/svg'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Popover from '@material-ui/core/Popover'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import classNames from 'classnames'
import React from 'react'
import styled from 'styled-components'
import * as css from './BulkUpdatePopOver.css'
import includes from 'lodash/includes'
import cloneDeep from 'lodash/cloneDeep'
import indexOf from 'lodash/indexOf'
import { useTranslation } from 'react-i18next'

interface option {
  label?: string
  value?: string
  color?: string
}
interface IProps {
  onApplyClick: any
  isInProgress: boolean
  data?: any
  selectedData?: any
  disabled?: boolean
  title?: string
  subtitle?: string
  applyToInstruction?: string
  applyToSelectedInstruction?: string
  status?: string
  statusColor?: string
  options?: Array<option>
  height?: number
  width?: number
  margin?: number
  isCheckboxOptions?: boolean
  noCustomHeight?: boolean
  tooltip?: string
  actionForMultiSelect?: any
  clearTableFilter?: any
  maxApplyLimit?: number
}
interface DivProps {
  height?: number
  width?: number
  margin?: number
  noCustomHeight?: boolean
}
interface SpanProps {
  color?: string
}

const DEFAULT_HEIGHT = 165
const DEFAULT_WIDTH = 200
const DEFAULT_MARGIN = 10

const StyledDiv = styled.div<DivProps>(props => {
  const { height, width, margin, noCustomHeight } = props
  return {
    '&&': {
      height: noCustomHeight ? 'unset' : height ? height : DEFAULT_HEIGHT,
      width: width ? width : DEFAULT_WIDTH,
      margin: margin ? margin : DEFAULT_MARGIN,
      display: 'flex',
      flexDirection: 'column'
    }
  }
})

const StyledSpan = styled.span<SpanProps>(props => {
  const { color } = props
  return {
    '&&': {
      borderBottom: `2px solid ${color}`
    }
  }
})

export const BulkUpdatePopOver: React.FunctionComponent<IProps> = ({
  height,
  width,
  margin,
  title,
  subtitle,
  applyToInstruction,
  applyToSelectedInstruction,
  status,
  onApplyClick,
  isInProgress,
  data,
  selectedData,
  disabled,
  options,
  statusColor,
  isCheckboxOptions,
  noCustomHeight,
  tooltip,
  actionForMultiSelect,
  clearTableFilter,
  maxApplyLimit
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [value, setValue] = React.useState('')
  const [checkedItems, setCheckedItems] = React.useState<any[]>([])

  const { t } = useTranslation()

  const handleCheckBoxToggle = (event: any) => {
    const selected: any[] = cloneDeep(checkedItems)
    if (event.target.checked) {
      selected.push(event?.target?.value as any)
    } else {
      const index = indexOf(selected, event.target.value)
      if (index > -1) {
        selected.splice(index, 1)
      }
    }
    setCheckedItems(selected)
  }
  const handleChange = event => {
    setValue(event.target.value)
    event.preventDefault()
    event.stopPropagation()
    event.nativeEvent.stopImmediatePropagation()
  }

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
    event.stopPropagation()
  }

  const handleClose = event => {
    setAnchorEl(null)
    event.stopPropagation()
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <div>
      <Tooltip title={disabled && tooltip ? tooltip : ''}>
        <img
          id={id}
          src={AssignmentIcon}
          className={classNames(css.actionIcon, { [css.disabled]: disabled })}
          onClick={disabled ? () => {} : handleClick}
          alt=''
        />
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <StyledDiv
          className={css.root}
          height={height}
          width={width}
          margin={margin}
          noCustomHeight={noCustomHeight}
        >
          {title ? (
            <Typography className={classNames([css.textFont, css.title])}>
              {title}
            </Typography>
          ) : null}
          {isCheckboxOptions ? (
            options?.map(option => (
              <FormControlLabel
                id={'value'}
                key={option.value}
                className={css.formLable}
                classes={{ root: css.formControlLabel }}
                control={
                  <Checkbox
                    onChange={handleCheckBoxToggle}
                    classes={{ root: css.checkbox }}
                    checked={includes(checkedItems, option.value)}
                    value={option.value}
                    color='default'
                  />
                }
                label={option.label}
              />
            ))
          ) : (
            <FormControl component='fieldset'>
              {subtitle ? (
                <div className={css.subtitleWrapper}>
                  <Typography className={classNames([css.textFont, css.subtitle])}>
                    {subtitle}
                  </Typography>
                  {status ? <StyledSpan color={statusColor}>{status}</StyledSpan> : null}
                </div>
              ) : null}
              <RadioGroup
                aria-label='bulkAction'
                name='bulkAction'
                className={css.radioGroup}
                value={value}
                onChange={handleChange}
              >
                {options?.map(option => (
                  <FormControlLabel
                    key={option.value}
                    className={css.radioButton}
                    value={option.value}
                    control={<Radio color='primary' />}
                    label={
                      option.color ? (
                        <StyledSpan color={option.color}>{option.label}</StyledSpan>
                      ) : (
                        option.label
                      )
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
          <div className={css.buttonContainer}>
            <Button variant='contained' size='small' onClick={handleClose}>
              {t('bb.common.label.cancel', 'Cancel')}
            </Button>
            {actionForMultiSelect ? (
              <Tooltip
                title={applyToSelectedInstruction ? applyToSelectedInstruction : ''}
              >
                <span>
                  <Button
                    variant='contained'
                    size='small'
                    disabled={
                      isCheckboxOptions
                        ? !checkedItems.length || isInProgress || !selectedData.length
                        : !value || isInProgress || !selectedData.length
                    }
                    onClick={onApplyClick(selectedData, value, clearTableFilter, true)}
                  >
                    {t('bb.common.label.applyToSelected', 'Apply To Selected')}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title={data.length > maxApplyLimit! ? applyToInstruction : ''}>
              <span>
                <Button
                  variant='contained'
                  size='small'
                  disabled={
                    isCheckboxOptions
                      ? !checkedItems.length ||
                        isInProgress ||
                        data.length > maxApplyLimit!
                      : !value || isInProgress || data.length > maxApplyLimit!
                  }
                  onClick={onApplyClick(data, value, clearTableFilter, true)}
                >
                  {t('bb.common.label.applyToAll', 'Apply To All')}
                </Button>
              </span>
            </Tooltip>
          </div>
        </StyledDiv>
      </Popover>
    </div>
  )
}
